import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis } from 'recharts';
import { Card } from '../../components/Card';
import type { ClusterResponse } from './insights';
import { HelpTip } from '../../components/HelpTip';

const COLOR_OK = '#10b981';
const COLOR_OUTLIER = '#ef4444';

// Confidence bins mirror components/ClassificationConfidenceHistogram.
const BINS = [
  { label: '<50%', min: 0, max: 0.5, fill: '#ef4444' },
  { label: '50–70%', min: 0.5, max: 0.7, fill: '#f59e0b' },
  { label: '70–85%', min: 0.7, max: 0.85, fill: '#facc15' },
  { label: '85–100%', min: 0.85, max: Infinity, fill: '#22c55e' },
];

export function MemberMapCard({ pages }: { pages: ClusterResponse['pages'] }) {
  const data = useMemo(
    () =>
      pages.map((p) => ({
        pageId: p.id,
        x: +(p.similarityToCentroid * 100).toFixed(1),
        y: +(p.classificationScore * 100).toFixed(1),
        fill: p.isOutlier ? COLOR_OUTLIER : COLOR_OK,
      })),
    [pages],
  );

  const histogram = useMemo(() => {
    const counts = BINS.map(() => 0);
    for (const p of pages) {
      const idx = BINS.findIndex((b) => p.classificationScore >= b.min && p.classificationScore < b.max);
      counts[idx === -1 ? BINS.length - 1 : idx]++;
    }
    return BINS.map((bin, i) => ({ name: bin.label, count: counts[i], fill: bin.fill }));
  }, [pages]);

  return (
    <Card
      header={
        <h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">
          Member Map{' '}
          <HelpTip text="Each point is a member page: x = similarity to the cluster centroid, y = classification confidence. Red = outlier." />
        </h2>
      }
    >
      <p className="text-xs text-gray-500 dark:text-zinc-400">
        Each point is a member page: x = similarity to the cluster centroid, y = classification confidence. Red = outlier.
      </p>
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 10, right: 16, left: 0, bottom: 16 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-zinc-200 dark:stroke-zinc-700" />
            <XAxis
              type="number"
              dataKey="x"
              domain={[0, 100]}
              tick={{ fontSize: 11 }}
              tickFormatter={(v) => `${v}%`}
              label={{ value: 'Similarity to centroid', position: 'insideBottom', offset: -8, fontSize: 12 }}
            />
            <YAxis
              type="number"
              dataKey="y"
              domain={[0, 100]}
              tick={{ fontSize: 11 }}
              tickFormatter={(v) => `${v}%`}
              label={{ value: 'Confidence', angle: -90, position: 'insideLeft', fontSize: 12 }}
            />
            <Tooltip
              contentStyle={{ fontSize: 12 }}
              formatter={(value, name) => [`${Number(value)}%`, name === 'x' ? 'Similarity' : 'Confidence']}
            />
            <Scatter data={data} fillOpacity={0.7} shape="circle">
              {data.map((d) => (
                <Cell key={d.pageId} fill={d.fill} />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      <h3 className="mt-2 text-xs font-medium text-gray-500 dark:text-zinc-400">Confidence histogram</h3>
      <div className="h-32 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={histogram} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-zinc-200 dark:stroke-zinc-700" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
            <Tooltip contentStyle={{ fontSize: 12 }} formatter={(value) => [`${Number(value)} pages`, 'Count']} />
            <Bar dataKey="count" radius={[3, 3, 0, 0]}>
              {histogram.map((entry) => (
                <Cell key={entry.name} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
